/**
 * Machine-readable API error codes. The server never returns user-facing prose
 * that the UI shows directly: the web client translates `errors.<code>`.
 * The `message` in the error body is an English fallback for logs and tooling.
 */
export const API_ERROR_CODES = [
  // generic
  "validation_failed",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "too_many_requests",
  "internal_error",
  "service_unavailable",
  // setup
  "setup_required",
  "setup_already_completed",
  "invalid_setup_code",
  // auth
  "invalid_credentials",
  "account_disabled",
  "invalid_current_password",
  "signup_disabled",
  // invitations
  "invitation_invalid",
  "invitation_not_pending",
  "invitation_no_email",
  "public_url_not_configured",
  // users
  "email_in_use",
  "last_admin",
  "user_not_found",
  "user_disabled",
  "reset_link_self",
  // password reset links
  "reset_link_invalid",
  // uploads and books
  "invalid_upload",
  "empty_file",
  "file_too_large",
  "unsupported_format",
  "malformed_file",
  "storage_full",
  "duplicate_file",
  "duplicate_pending_upload",
  "pending_upload_limit",
  "upload_already_resolved",
  "book_not_found",
  "upload_not_found",
  // catalog
  "edit_conflict",
  "name_conflict",
  "invalid_image",
  "cover_not_found",
  "file_not_found",
  "file_missing",
  // shelves
  "shelf_not_found",
  "shelf_name_taken",
  "shelf_limit_reached",
  "shelf_full",
  "smart_shelf_read_only",
  "unknown_books",
  "book_not_on_shelf",
  // reading tracking
  "reading_session_active",
  "reading_session_ended",
  "reading_session_not_found",
  // ratings
  "rating_not_found",
  // device tokens
  "device_token_limit",
  "device_token_not_found",
  // background jobs
  "job_not_found",
  "job_not_cancellable",
  "job_type_unknown",
  // calibre import
  "calibre_path_not_allowed",
  "calibre_library_not_found",
  "calibre_db_unreadable",
  "calibre_db_unsupported_schema",
  "calibre_import_in_progress",
  // KOReader sync
  "digest_backfill_in_progress",
  // instance settings and metadata lookup
  "setting_unknown",
  "metadata_lookup_disabled",
  "isbn_invalid",
  "metadata_query_required",
  "metadata_providers_unavailable",
  "provider_error",
  "provider_quota_exceeded",
  "google_api_key_invalid",
  "metadata_candidate_not_found",
  "metadata_cover_invalid",
  "metadata_cover_fetch_failed",
  "book_modified",
  "rate_limited",
  // author suggestions
  "author_suggestions_disabled",
  "author_suggestions_cooldown",
  "suggestion_not_found",
  "suggestion_read_undo_expired",
  "author_mapping_id_invalid",
  // email and send to kindle
  "email_not_configured",
  "kindle_email_invalid",
  "kindle_email_missing",
  "no_kindle_compatible_format",
  "kindle_delivery_pending",
  "delivery_interrupted",
  "smtp_connection_failed",
  "smtp_auth_failed",
  "smtp_tls_required",
  "smtp_rejected",
  "smtp_send_failed",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/**
 * Codes used as values in `fields` (field-level validation errors).
 * They are also used as Zod issue messages in the shared schemas, so the same
 * code reaches the UI whether validation ran on the client or on the server.
 */
export const FIELD_ERROR_CODES = [
  "required",
  "invalid",
  "email_invalid",
  "email_in_use",
  "email_mismatch",
  "name_required",
  "name_too_long",
  "password_too_short",
  "password_too_long",
  "passwords_mismatch",
  "setup_code_required",
  "invalid_current_password",
  // catalog editing
  "too_long",
  "too_many",
  "out_of_range",
  "invalid_date",
  "invalid_language",
  "invalid_isbn",
  "series_required",
  "duplicate_identifier_type",
  // reading tracking
  "future_date",
  "before_start",
  "both_end_dates",
  "date_required",
] as const;

export type FieldErrorCode = (typeof FIELD_ERROR_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    /** Field path (dot-separated) → field error code. */
    fields?: Record<string, string>;
    /** Machine-readable context, e.g. `{ bookId }` for `duplicate_file`. */
    details?: Record<string, string | number>;
  };
}

export function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === "string" && (API_ERROR_CODES as readonly string[]).includes(value);
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== "object" || value === null || !("error" in value)) return false;
  const error = (value as { error: unknown }).error;
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "string"
  );
}
